import { QueryResult } from '@apollo/client';
import {
  Address,
  Client,
  Conversation,
  GetAddressesQuery,
  GetConversationQuery,
  GetPlatformUserQuery,
  GetProjectsQuery,
  Project,
  ServerPlatformUserRaw,
} from 'src/graphql';
import { ConditionInput, EntityNames, SortingInput } from 'src/graphql/types';
import { CacheManager } from 'src/utils/hooks/use-cache-manager';
import { useSearchAddresses } from './search-address';
import { GetClientsQuery, useSearchClients } from './search-client';
import { useSearchConversations } from './search-conversation';
import { useSearchPlatformUsers } from './search-platform-user';
import { useSearchProjects } from './search-project';

export const DEFAULT_PAGINATION_LIMIT = 10;

export type AnySearchableEntity = SearchEntityReturnType[keyof SearchEntityReturnType];

export type SearchQueryByEntityType<T> = T extends Conversation
  ? GetConversationQuery
  : T extends Client
    ? GetClientsQuery
    : T extends Address
      ? GetAddressesQuery
      : T extends Project
        ? GetProjectsQuery
        : T extends ServerPlatformUserRaw
          ? GetPlatformUserQuery
          : never;

interface UseSearchResponseParams<T extends AnySearchableEntity> extends QueryResult {
  records: T[];
  loadMore: (limit?: number, localOffset?: number) => Promise<void>;
  totalRecords: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  cacheManager: CacheManager<SearchQueryByEntityType<T>>;
}

export interface SearchEntityReturnType {
  [EntityNames.Conversation]: Conversation;
  [EntityNames.Client]: Client;
  [EntityNames.Site]: Address;
  [EntityNames.Project]: Project;
  [EntityNames.PlatformUser]: ServerPlatformUserRaw;
}

/**
 * Custom hook to search different entities.
 *
 * Initializes all relevant search hooks to enable searching multiple entities on the same page.
 * Since React hooks cannot be conditionally rendered, all hooks are initialized and only the
 * selected entity's hook is active while others are disabled.
 *
 * @param params - Search parameters including entity name, term, filters, and limit.
 * @returns Search results and related metadata for the specified entity, or `null` if unsupported.
 */
export const useSearch = <T extends EntityNames>(
  params: {
    entityName: T;
    searchTerm: string | null;
    filter?: ConditionInput[];
    sort?: SortingInput | SortingInput[];
    limit?: number;
    skip?: boolean;
    showResultsWhenEmptySearchTerm?: boolean;
    targetIdAttributes?: string[];
    shouldIncludePlatformPartnerId?: boolean;
    partnerId?: number | null;
    dynamicAttributePaths?: string[];
    includeDeleted?: boolean;
  },
  options?: { skip?: boolean },
): UseSearchResponseParams<SearchEntityReturnType[T]> | null => {
  const {
    searchTerm,
    filter,
    limit = DEFAULT_PAGINATION_LIMIT,
    entityName,
    skip,
    showResultsWhenEmptySearchTerm,
    sort,
    targetIdAttributes,
    shouldIncludePlatformPartnerId,
    partnerId,
    dynamicAttributePaths,
    includeDeleted,
  } = params;

  const conversation = useSearchConversations({
    skip: entityName !== EntityNames.Conversation || options?.skip || skip,
    searchTerm,
    // we should not pass filters from wrong entities otherwise they will be triggered as
    // soon as the entity name changes
    filter: entityName === EntityNames.Conversation ? filter : undefined,
    limit,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    dynamicAttributePaths,
    includeDeleted,
  });

  const client = useSearchClients({
    skip: entityName !== EntityNames.Client || options?.skip || skip,
    searchTerm,
    filter: entityName === EntityNames.Client ? filter : undefined,
    limit,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    dynamicAttributePaths,
    includeDeleted,
  });

  const site = useSearchAddresses({
    searchTerm,
    skip: entityName !== EntityNames.Site || options?.skip || skip,
    filter: entityName === EntityNames.Site ? filter : undefined,
    limit,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    dynamicAttributePaths,
    includeDeleted,
  });

  const project = useSearchProjects({
    searchTerm,
    skip: entityName !== EntityNames.Project || options?.skip || skip,
    filter: entityName === EntityNames.Project ? filter : undefined,
    limit,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    partnerId,
    dynamicAttributePaths,
    includeDeleted,
  });

  const platformUser = useSearchPlatformUsers({
    searchTerm,
    skip: entityName !== EntityNames.PlatformUser || options?.skip || skip,
    filter: entityName === EntityNames.PlatformUser ? filter : undefined,
    limit,
    sort,
    shouldIncludePlatformPartnerId,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    partnerId,
  });

  const data = {
    [EntityNames.Conversation]: conversation,
    [EntityNames.Client]: client,
    [EntityNames.Site]: site,
    [EntityNames.Project]: project,
    [EntityNames.PlatformUser]: platformUser,
  };

  return (
    (data[entityName] as unknown as UseSearchResponseParams<SearchEntityReturnType[T]>) ?? null
  );
};
